import { createContext, useContext } from 'react';
import type { PaymentStepContextValue, PaymentStepDependencies } from './types';
import { usePaymentStep } from './usePaymentStep';

const PaymentStepContext = createContext<PaymentStepContextValue | undefined>(undefined);

export const usePaymentStepContext = () => {
  const context = useContext(PaymentStepContext);
  if (!context) {
    throw new Error('usePaymentStepContext must be used within PaymentStepProvider');
  }
  return context;
};

/**
 * Cross-platform PaymentStepProvider using dependency injection architecture.
 *
 * This provider accepts platform-specific dependencies and combines them with
 * cross-platform business logic from usePaymentStep. This eliminates the need
 * for separate web and mobile context implementations.
 *
 * @param dependencies - Platform-specific dependencies (web or mobile)
 * @param children - Child components that consume the payment step context
 */
export const PaymentStepProvider = ({
  children,
  dependencies,
}: {
  children: React.ReactNode;
  dependencies: PaymentStepDependencies;
}) => {
  // Use cross-platform payment step logic with injected dependencies
  const contextValue = usePaymentStep(dependencies);

  return <PaymentStepContext.Provider value={contextValue}>{children}</PaymentStepContext.Provider>;
};
